// src/App.jsx
import React, { useState, useEffect, lazy, Suspense } from "react";
import * as Sentry from "@sentry/react";
import "./App.css";
import { Routes, Route, useLocation } from "react-router-dom";
import { Grid } from "semantic-ui-react";
import { Auth, Hub } from "aws-amplify";

import { PageLayout } from "./components/PageLayout";
import WalkthroughMount from "./components/walkthrough/WalkthroughMount";
import { RecordAccessGate } from "./components/RecordAccessGate";
import LoginPage from "./components/LoginPage";
import { RequireOrganization } from "./pages/ChooseOrganization";
import { initAnalytics, trackPageView } from "./utils/analytics";
import { useRole } from "./hooks/useRole.ts";
import { useOrganization } from "./hooks/useOrganization";
import { loadPermittedOrganizations } from "./utils/apiClient";

/* Chunk-hashed filenames change on every production deploy, so a tab left
   open across a deploy 404s when it lazy-loads a page it hasn't visited yet
   ("Loading chunk failed"). Reload once to pick up the new build; if the
   import still fails after a fresh load, surface the real error. */
const lazyRetry = (importer) =>
  lazy(() =>
    importer().then(
      (module) => {
        sessionStorage.removeItem("chunk-reload");
        return module;
      },
      (error) => {
        if (!sessionStorage.getItem("chunk-reload")) {
          sessionStorage.setItem("chunk-reload", "1");
          window.location.reload();
          return new Promise(() => {}); // stay on the Suspense fallback while reloading
        }
        throw error;
      },
    ),
  );

/* ---- shell ---- */
const HomePage = lazyRetry(() => import("./pages/HomePage"));
const AdminPage = lazyRetry(() => import("./pages/AdminPage"));
const AnalyticsPage = lazyRetry(() => import("./pages/AnalyticsPage"));
const HelpPage = lazyRetry(() => import("./pages/HelpPage"));

/* ---- warehouse / inventory ---- */
const WarehouseInventory = lazyRetry(
  () => import("./components/WarehouseInventory"),
);
const WarehouseInventoryDetails = lazyRetry(
  () => import("./components/WarehouseInventoryDetails"),
);
const TagList = lazyRetry(() => import("./components/TagList"));
const TagListCompany = lazyRetry(() => import("./components/TagListCompany"));
const PrintTags = lazyRetry(() => import("./components/PrintTags"));

/* ---- bills of lading ---- */
const ViewBOLs = lazyRetry(() => import("./components/ViewBOLs"));
const EditBOL = lazyRetry(() => import("./components/EditBOL"));
const BOLForm = lazyRetry(() => import("./components/BOLForm"));
const PrintBOLTemplate = lazyRetry(
  () => import("./pages/PrintBOLTemplate.jsx"),
);
const PrintBOLTemplateBlank = lazyRetry(
  () => import("./contractor-slice/components/PrintBOLTemplateBlank"),
);

/* ---- storage billing (contractor-slice) ---- */
const PrintStorageBilling = lazyRetry(
  () => import("./contractor-slice/components/PrintStorageBilling"),
);

/* ---- companies, jobs, locations, contacts ---- */
const CompanySearch = lazyRetry(() => import("./components/CompanySearch"));
const CompanyDetailsLoader = lazyRetry(
  () => import("./components/CompanyDetailsLoader"),
);
const CreateCompany = lazyRetry(() => import("./components/CreateCompany"));
const EditCompany = lazyRetry(() => import("./components/EditCompany"));
const CreateContact = lazyRetry(() => import("./components/CreateContact"));
const EditContact = lazyRetry(() => import("./components/EditContact"));
const CreateLocation = lazyRetry(
  () => import("./components/CreateLocation.jsx"),
);
const EditLocation = lazyRetry(() => import("./components/EditLocation"));
const JobsList = lazyRetry(() => import("./pages/JobsList"));
const JobForm = lazyRetry(() => import("./components/JobForm"));

/* ---- administration ---- */
const UserManagement = lazyRetry(() => import("./components/UserManagement"));
const PersonnelManagement = lazyRetry(
  () => import("./components/PersonnelManagement"),
);
const EditPersonnel = lazyRetry(() => import("./components/EditPersonnel"));

/* ---- public (no auth) pages ---- */
const CustomerInventoryPortal = lazyRetry(
  () => import("./pages/CustomerInventoryPortal"),
);

/* Route guard: blocks read-only roles from pages they should not see at all
   (warehouse/tags). Mirrors the nav gating in PageLayout. */
function RequireEditor({ children }) {
  const { isReadOnly, loading } = useRole();
  if (loading) return null;
  if (isReadOnly) {
    return (
      <div style={{ padding: 24 }}>
        <h3>
          You have view-only access. This page requires an Admin or Salesperson
          role.
        </h3>
      </div>
    );
  }
  return children;
}

function App() {
  const location = useLocation();
  // Keys the main route table: a switch re-mounts the routed screen, so a
  // list fetched under the other organization is never left on the page.
  const { id: organization } = useOrganization();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  // Which organizations this user may use, asked once signed in. The shell
  // waits for it, so a restricted user never sees the switch or the chooser
  // offer the organization they do not have.
  const [accessLoaded, setAccessLoaded] = useState(false);

  useEffect(() => {
    // Listen for Hub auth events so we never miss a sign-in completing.
    // Amplify exchanges the auth code asynchronously, so
    // currentAuthenticatedUser() may fail if called too early.
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signIn" || payload.event === "cognitoHostedUI") {
        setIsAuthenticated(true);
        setCheckingAuth(false);
      }
      if (
        payload.event === "signIn_failure" ||
        payload.event === "cognitoHostedUI_failure"
      ) {
        setCheckingAuth(false);
      }
    });

    Auth.currentAuthenticatedUser()
      .then(() => setIsAuthenticated(true))
      .catch(() => setIsAuthenticated(false))
      .finally(() => setCheckingAuth(false));

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let live = true;
    loadPermittedOrganizations().finally(() => {
      if (live) setAccessLoaded(true);
    });
    return () => {
      live = false;
    };
  }, [isAuthenticated]);

  // Usage analytics - global click listener + flush loop. Only after login;
  // initAnalytics/trackPageView no-op on the public /customer/inventory/* route.
  useEffect(() => {
    if (isAuthenticated) initAnalytics();
  }, [isAuthenticated]);

  // Page-view tracking on every route change
  useEffect(() => {
    if (isAuthenticated) trackPageView(location.pathname);
  }, [location.pathname, isAuthenticated]);

  // Public routes bypass authentication entirely
  if (location.pathname.startsWith("/customer/inventory/")) {
    return (
      <div className="App">
        <Sentry.ErrorBoundary
          fallback={({ error, resetError }) => (
            <div style={{ padding: 40, textAlign: "center" }}>
              <h2>Something went wrong</h2>
              <p>{error?.message}</p>
              <button onClick={resetError}>Try again</button>
            </div>
          )}
        >
          <Suspense
            fallback={
              <div style={{ padding: 40, textAlign: "center" }}>Loading...</div>
            }
          >
            <Routes>
              <Route
                path="/customer/inventory/:token"
                element={<CustomerInventoryPortal />}
              />
            </Routes>
          </Suspense>
        </Sentry.ErrorBoundary>
      </div>
    );
  }

  if (checkingAuth || (isAuthenticated && !accessLoaded)) {
    return (
      <div
        className="App"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="App">
        <LoginPage onAuthenticated={() => setIsAuthenticated(true)} />
      </div>
    );
  }

  return (
    <div className="App">
      <Sentry.ErrorBoundary
        fallback={({ error, resetError }) => (
          <div style={{ padding: 40, textAlign: "center" }}>
            <h2>Something went wrong</h2>
            <p>{error?.message}</p>
            <button onClick={resetError}>Try again</button>
          </div>
        )}
      >
        {/* The first-run chooser stands in for the whole shell until an
            organization has been chosen in this browser. */}
        <RequireOrganization>
          <Grid padded>
            <Grid.Column>
              <PageLayout />
              {/* The first sign-in walkthrough: outside the route table so
                  it lives across its own navigation, and inside
                  RequireOrganization so it waits for the chooser. */}
              <WalkthroughMount />

              {/* ---------- MAIN ROUTE TABLE ---------- */}
              <Suspense
                fallback={
                  <div style={{ padding: 40, textAlign: "center" }}>
                    Loading...
                  </div>
                }
              >
                {/* A record the router refused shows a message in its place. */}
                <RecordAccessGate>
                  <Routes key={organization}>
                    {/* shell */}
                    <Route path="/" element={<HomePage />} />
                    <Route path="/AdminPage" element={<AdminPage />} />
                    <Route path="/Analytics" element={<AnalyticsPage />} />
                    <Route path="/Help" element={<HelpPage />} />

                    {/* warehouse / inventory - blocked for read-only roles */}
                    <Route
                      path="/WarehouseInventory"
                      element={
                        <RequireEditor>
                          <WarehouseInventory />
                        </RequireEditor>
                      }
                    />
                    <Route
                      path="/WarehouseInventory/:TDControlNumber/:TagNo"
                      element={
                        <RequireEditor>
                          <WarehouseInventoryDetails />
                        </RequireEditor>
                      }
                    />

                    {/* tags - TagList stays open to read-only roles: the job
                form's "Current Inventory" button links here for them */}
                    <Route
                      path="/TagList/:TDControlNumber"
                      element={<TagList />}
                    />
                    <Route
                      path="/TagList/:TDControlNumber/*"
                      element={<TagList />}
                    />
                    <Route
                      path="/TagListCompany/:CustomerID"
                      element={
                        <RequireEditor>
                          <TagListCompany />
                        </RequireEditor>
                      }
                    />
                    <Route
                      path="/TagListCompany/:CustomerID/*"
                      element={
                        <RequireEditor>
                          <TagListCompany />
                        </RequireEditor>
                      }
                    />
                    <Route
                      path="/PrintTags/:TDControlNumber"
                      element={
                        <RequireEditor>
                          <PrintTags />
                        </RequireEditor>
                      }
                    />

                    {/* bills of lading */}
                    <Route
                      path="/ViewBOLs/:TDControlNumber"
                      element={<ViewBOLs />}
                    />
                    <Route
                      path="/ViewBOLs/:TDControlNumber/EditBOL/:BOLID"
                      element={<EditBOL />}
                    />
                    <Route
                      path="/CreateBOL/:TDControlNumber"
                      element={<BOLForm />}
                    />
                    <Route
                      path="/EditBOL/:TDControlNumber/:BOLID"
                      element={<BOLForm />}
                    />
                    <Route
                      path="/PrintBOLTemplate/:TDControlNumber/:BOLID"
                      element={<PrintBOLTemplate />}
                    />
                    <Route
                      path="/PrintBOLTemplateBlank"
                      element={<PrintBOLTemplateBlank />}
                    />

                    {/* storage billing */}
                    <Route
                      path="/PrintStorageBilling"
                      element={<PrintStorageBilling />}
                    />

                    {/* companies, contacts, locations */}
                    <Route path="/CompaniesList" element={<CompanySearch />} />
                    <Route
                      path="/CompaniesList/:CustomerID"
                      element={<CompanyDetailsLoader />}
                    />
                    <Route path="/CompanySearch" element={<CompanySearch />} />
                    <Route path="/CreateCompany" element={<CreateCompany />} />
                    <Route
                      path="/EditCompany/:CustomerID"
                      element={<EditCompany />}
                    />
                    <Route path="/CreateContact" element={<CreateContact />} />
                    <Route
                      path="/CreateContact/:CustomerID"
                      element={<CreateContact />}
                    />
                    <Route
                      path="/EditContact/:CustomerID/:ContactID"
                      element={<EditContact />}
                    />
                    <Route
                      path="/CreateLocation/:CustomerID"
                      element={<CreateLocation />}
                    />
                    <Route
                      path="/EditLocation/:CustomerID/:LocationID"
                      element={<EditLocation />}
                    />

                    {/* jobs (receiving tickets) */}
                    <Route path="/JobsList" element={<JobsList />} />
                    <Route path="/CreateJob" element={<JobForm />} />
                    <Route
                      path="/CreateJob/:BillingCustomerID"
                      element={<JobForm />}
                    />
                    <Route
                      path="/EditJob/:BillingCustomerID/:JobTicketID"
                      element={<JobForm />}
                    />

                    {/* administration */}
                    <Route
                      path="/UserManagement"
                      element={<UserManagement />}
                    />
                    <Route
                      path="/PersonnelManagement"
                      element={<PersonnelManagement />}
                    />
                    <Route
                      path="/EditPersonnel/:EmployeeID"
                      element={<EditPersonnel />}
                    />
                  </Routes>
                </RecordAccessGate>
              </Suspense>
            </Grid.Column>
          </Grid>
        </RequireOrganization>
      </Sentry.ErrorBoundary>
    </div>
  );
}
export default App;
